/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import { FC, useEffect } from 'react';
import { X } from 'lucide-react';
import { useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import HotdeskButton from '@/hotdesk-ui/components/shared/HotdeskButton';
import { useListingsFetch } from './useListingsFetch';
import styles from './WorkspaceListings.module.scss';
import ListingCard from '@/hotdesk-ui/components/shared/ListingCard';
interface WorkspaceListingsProps {
  showPopup: boolean;
  workspaceId: number;
  workspaceName: string;
  languageCode: string;
  onClose: () => void;
  showBookNow?: boolean;
}
const FALLBACK_IMAGE = 'https://hotdesk-assets.s3.eu-west-1.amazonaws.com/landing_pages/workspace_directory/defaultImage.webp';
const WorkspaceListings: FC<WorkspaceListingsProps> = ({
  showPopup,
  workspaceId,
  workspaceName,
  languageCode,
  onClose,
  showBookNow = false
}) => {
  const token = useAppSelector((state: RootState) => state.auth.token);
  const {
    listings,
    loading,
    loadingMore,
    error,
    totalCount,
    hasMore,
    fetchListings,
    loadMore,
    reset
  } = useListingsFetch({
    workspaceId,
    languageCode,
    token
  });
  useEffect(() => {
    if (showPopup && workspaceId) {
      reset();
      fetchListings(10, 0, false);
    }
  }, [showPopup, workspaceId, fetchListings, reset]);
  useEffect(() => {
    if (!showPopup) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPopup]);
  const handleClose = () => {
    reset();
    onClose();
  };
  if (!showPopup) return null;
  return <div className={styles.backdrop} onClick={e => {
    if (e.target === e.currentTarget) handleClose();
  }} data-sentry-component="WorkspaceListings" data-sentry-source-file="WorkspaceListings.tsx">
            <div className={styles.card} role="dialog" aria-modal="true" aria-label={`Workspace Listings for ${workspaceName}`}>
                <div className={styles.header}>
                    <div className={styles.heading}>
                        <p className={styles.eyebrow}>{workspaceName}</p>
                        <h2 className={styles.title}>
                            Workspace Listings
                            {totalCount > 0 && <span className={styles.count}>
                                    {' '}
                                    ({totalCount})
                                </span>}
                        </h2>
                    </div>
                    <button type="button" className={styles.closeBtn} onClick={handleClose} aria-label="Close">
                        <X size={18} strokeWidth={2} data-sentry-element="X" data-sentry-source-file="WorkspaceListings.tsx" />
                    </button>
                </div>

                <div className={styles.body}>
                    {loading && <div className={styles.statusRow}>
                            <span className={styles.spinner} />
                            <p className={styles.statusText}>
                                Loading Listings…
                            </p>
                        </div>}

                    {error && !loading && <div className={styles.statusRow}>
                            <p className={styles.statusText}>
                                Couldn’t load listings. Please try again.
                            </p>
                        </div>}

                    {!loading && !error && listings.length > 0 && <ul className={styles.list}>
                            {listings.map((listing, idx) => {
            return <li key={listing.id} className={styles.workspaceItem}>
                                        <ListingCard key={listing.id || idx} listing={listing} variant="listing" showBookNow={showBookNow} reviewsScreen="listing_workspace" />
                                    </li>;
          })}
                        </ul>}

                    {hasMore && !loading && !error && <div className={styles.loadMoreRow}>
                            <HotdeskButton variant="secondary" size="md" onClick={loadMore} disabled={loadingMore}>
                                {loadingMore ? 'Loading…' : 'View more'}
                            </HotdeskButton>
                        </div>}
                </div>
            </div>
        </div>;
};
export default WorkspaceListings;